// src/components/texting/TextMilestoneCard.jsx
// "Milestone update" approval card — appears on the deal overview when the
// deal reaches one of the configured stages (Settings → Automated Texts,
// milestone switch) and that stage hasn't been texted yet. NEVER auto-sends.
import React, { useEffect, useState } from "react";
import { PartyPopper, Send } from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { updateItem, getContactsByIds } from "../../utils/firebasefunctions";
import { normalizeContact } from "../../utils/contactNormalize";
import {
  canUseTexting,
  getTextingSettings,
  renderTemplate,
  parseStageList,
  firstNameOf,
  normalizePhoneE164,
  sendText,
} from "../../utils/texting";

export default function TextMilestoneCard({ deal, dealId: dealIdProp, onDealUpdate }) {
  const [{ user }] = useStateValue();
  const [settings, setSettings] = useState(null);
  const [phone, setPhone] = useState("");
  const [clientName, setClientName] = useState("");
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const dealId = dealIdProp || deal?.id || "";
  const stage = deal?.dealstage || "";
  const contactId = Array.isArray(deal?.customers) ? deal.customers[0] : "";
  const address =
    deal?.propertyaddress || deal?.propertyName || deal?.address || "your home";

  useEffect(() => {
    getTextingSettings().then(setSettings);
  }, []);

  const qualifies =
    !!settings?.milestoneEnabled &&
    canUseTexting(user) &&
    !deal?.doNotText &&
    !!stage &&
    parseStageList(settings?.milestoneStages).includes(stage) &&
    deal?.lastMilestoneTexted !== stage;

  useEffect(() => {
    if (!qualifies || !contactId) return;
    let alive = true;
    (async () => {
      try {
        const raw = (await getContactsByIds([contactId]))[0];
        const c = normalizeContact(raw);
        if (alive) {
          setPhone(normalizePhoneE164(c?.phone || ""));
          setClientName(c?.name || "");
        }
      } catch (e) {
        console.error("milestone phone lookup failed:", e);
      }
    })();
    return () => {
      alive = false;
    };
  }, [qualifies, contactId]);

  useEffect(() => {
    if (settings && stage) {
      setDraft(
        renderTemplate(settings.milestoneTemplate, {
          firstName: firstNameOf(clientName),
          address,
          stage,
        }),
      );
    }
  }, [settings, stage, clientName, address]);

  if (!qualifies || !phone || !dealId) return null;

  const markHandled = async () => {
    await updateItem("deals", dealId, { lastMilestoneTexted: stage });
    onDealUpdate?.({ ...deal, lastMilestoneTexted: stage });
  };

  const send = async () => {
    setBusy(true);
    const res = await sendText({
      to: phone,
      body: draft,
      dealId,
      contactName: clientName || deal?.clientLastName || "",
      user,
      source: "milestone",
    });
    setBusy(false);
    if (res.ok) {
      toast.success(res.queued ? "Queued — sends after quiet hours" : "Milestone text sent");
      await markHandled();
    } else {
      toast.error(res.error || "Text failed to send");
    }
  };

  return (
    <div className="border border-emerald-200 bg-emerald-50/60 rounded-xl p-3 mb-4">
      <div className="flex items-center gap-2 text-[13px] font-semibold text-emerald-800">
        <PartyPopper size={14} /> Milestone reached · {stage} — text the client?
      </div>
      {editing ? (
        <textarea
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="mt-2 w-full text-sm border border-emerald-300 rounded-lg px-3 py-2 bg-white"
        />
      ) : (
        <p className="mt-2 text-sm text-slate-700 bg-white border border-emerald-100 rounded-lg px-3 py-2 whitespace-pre-wrap">
          {draft}
        </p>
      )}
      <div className="flex gap-2 mt-2">
        <button
          onClick={send}
          disabled={busy || !draft.trim()}
          className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#1E293B] text-white rounded-lg px-3 py-1.5 disabled:opacity-50"
        >
          <Send size={12} /> Send text
        </button>
        <button
          onClick={() => setEditing((v) => !v)}
          className="text-xs font-semibold border border-slate-300 text-slate-600 rounded-lg px-3 py-1.5 hover:bg-white"
        >
          {editing ? "Done editing" : "Edit"}
        </button>
        <button
          onClick={markHandled}
          className="text-xs font-semibold border border-slate-300 text-slate-600 rounded-lg px-3 py-1.5 hover:bg-white"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
